import type { ReactElement, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type {
    InfiniteScrollListCursor,
} from '@/components/ui/infinite-scroll-list.types';
import {
    DefaultInfiniteScrollListStateCopy,
    InfiniteScrollListEmptyState,
    InfiniteScrollListErrorState,
    InfiniteScrollListLoadingState,
    type InfiniteScrollListStateCopy,
} from '@/components/ui/infinite-scroll-list-state';
import {
    useInfiniteScrollList,
    type UseInfiniteScrollListOptions,
} from '@/hooks/useInfiniteScrollList';

interface InfiniteScrollListRenderContext<
    TItem,
    TCursor extends InfiniteScrollListCursor,
> {
    readonly items: ReadonlyArray<TItem>;
    readonly isFetching: boolean;
    readonly isFetchingNextPage: boolean;
    readonly hasNextPage: boolean;
    readonly pagination: ReturnType<
        typeof useInfiniteScrollList<TItem, TCursor, readonly unknown[]>
    >['pagination'];
}

export interface InfiniteScrollListProps<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
> extends Pick<
        UseInfiniteScrollListOptions<TItem, TCursor, TQueryKeyPrefix>,
        | 'queryKeyPrefix'
        | 'pageSize'
        | 'fetchPage'
        | 'parseCursorParam'
        | 'initialQueryText'
        | 'enabled'
        | 'retry'
        | 'staleTime'
        | 'gcTime'
        | 'rootMargin'
        | 'threshold'
    > {
    pageTitle: string;
    cardTitle?: string;
    queryPlaceholder: string;
    stateCopy?: Partial<InfiniteScrollListStateCopy>;
    headerActions?: ReactNode;
    controls?: ReactNode;
    renderItems: (
        context: InfiniteScrollListRenderContext<TItem, TCursor>
    ) => ReactNode;
}

export function InfiniteScrollList<
    TItem,
    TCursor extends InfiniteScrollListCursor,
    TQueryKeyPrefix extends readonly unknown[],
>(props: InfiniteScrollListProps<TItem, TCursor, TQueryKeyPrefix>): ReactElement {
    const stateCopy: InfiniteScrollListStateCopy = {
        ...DefaultInfiniteScrollListStateCopy,
        ...props.stateCopy,
    };
    const list = useInfiniteScrollList<TItem, TCursor, TQueryKeyPrefix>({
        queryKeyPrefix: props.queryKeyPrefix,
        pageSize: props.pageSize,
        fetchPage: props.fetchPage,
        parseCursorParam: props.parseCursorParam,
        initialQueryText: props.initialQueryText,
        enabled: props.enabled,
        retry: props.retry,
        staleTime: props.staleTime,
        gcTime: props.gcTime,
        rootMargin: props.rootMargin,
        threshold: props.threshold,
    });

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <h1 className="text-3xl font-bold">{props.pageTitle}</h1>
                {props.headerActions ?? null}
            </div>

            <Card>
                {props.cardTitle !== undefined ? (
                    <CardHeader>
                        <CardTitle>{props.cardTitle}</CardTitle>
                    </CardHeader>
                ) : null}
                <CardContent>
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <Input
                            placeholder={props.queryPlaceholder}
                            value={list.queryText}
                            onChange={(event) => {
                                list.setQueryText(event.target.value);
                            }}
                            className="w-[320px]"
                        />
                        {props.controls ?? null}
                    </div>

                    {list.error !== null ? (
                        <InfiniteScrollListErrorState
                            error={list.error}
                            genericErrorLabel={stateCopy.genericErrorLabel}
                        />
                    ) : list.isLoading && list.items.length === 0 ? (
                        <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
                    ) : list.items.length === 0 ? (
                        <InfiniteScrollListEmptyState label={stateCopy.emptyLabel} />
                    ) : (
                        <>
                            {props.renderItems({
                                items: list.items,
                                isFetching: list.isFetching,
                                isFetchingNextPage: list.isFetchingNextPage,
                                hasNextPage: list.hasNextPage,
                                pagination: list.pagination,
                            })}
                            <div ref={list.loadMoreSentinelRef} className="h-8" />
                            {list.isFetching ? (
                                <InfiniteScrollListLoadingState label={stateCopy.loadingLabel} />
                            ) : null}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
